import React, { useEffect, useState } from "react";
import "./styles/Chat.css";

// Hooks
import { useOfficeInitialization } from "./hooks/useOfficeInitialization";
import { useOfficeContent } from "./hooks/useOfficeContent";
import { useChat } from "./hooks/useChat";

// Components
import { ChatArea } from "./components/chat/ChatArea";
import { ChatInput } from "./components/chat/ChatInput";
import { ErrorMessage } from "./components/common/ErrorMessage";
import { LoginButton } from "./components/common/LoginButton";
import { KnowledgeBaseId, ModelId, MODEL_OPTIONS } from "./types";

import { createNestablePublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

export default function App() {
  // Office initialization
  const { 
    context,
    item, 
    mode, 
    isReady, 
    userName, 
    userEmail,
    senderName, 
    isLicenseError,
    requiresPopup,
    loginHint,
    availableModels,
    knowledgeBases,
    onPopupSuccess
  } = useOfficeInitialization();

  // Office content management (works for Outlook, Word, Excel, PowerPoint)
  const { 
    emailBodyText, 
    hasOriginalEmail, 
    updateEmailBodyText,
    getCurrentContentText,
    getSelection
  } = useOfficeContent(context, mode, isReady, item);

  // Track insert operation state (must be before any conditional returns)
  const [isInserting, setIsInserting] = useState(false);

  // Initialize with first available model/knowledge base from API, fallback to hardcoded if not available
  const [selectedModel, setSelectedModel] = useState<ModelId>(
    availableModels.length > 0 ? availableModels[0].id : MODEL_OPTIONS[0].id
  );
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBaseId>('none');

  // Update selected model/knowledge base when API data becomes available
  useEffect(() => {
    if (availableModels.length > 0 && !availableModels.find(m => m.id === selectedModel)) {
      setSelectedModel(availableModels[0].id);
    }
  }, [availableModels, selectedModel]);

  // Knowledge base always defaults to 'none', but update if current selection is invalid
  useEffect(() => {
    if (selectedKnowledgeBase !== 'none' && knowledgeBases.length > 0 && !knowledgeBases.find(kb => kb.id === selectedKnowledgeBase)) {
      setSelectedKnowledgeBase('none');
    }
  }, [knowledgeBases, selectedKnowledgeBase]);

  // Chat functionality
  const { 
    chat, 
    setChat, 
    input, 
    setInput, 
    isStreaming, 
    handleAction,
    initializeChat
  } = useChat(
    userName, 
    senderName, 
    mode, 
    emailBodyText, 
    updateEmailBodyText, 
    getCurrentContentText, 
    selectedModel, 
    selectedKnowledgeBase, 
    context?.host || 'Unknown',
    getSelection, // Get selection from useOfficeContent hook
    knowledgeBases // Pass knowledge bases array
  );

  // Initialize chat with welcome message when ready
  // If license error, show license warning message; if no models, show model warning; otherwise show normal greeting
  useEffect(() => {
    if (isReady && userName) {
      if (isLicenseError) {
        // Show greeting with license warning when license is inactive
        setChat([{ 
          role: "assistant", 
          content: `Hello${userName ? " " + userName : ""}! There's no active license for your account. Please contact support to activate your license.` 
        }]);
      } else if (availableModels.length === 0) {
        // Show greeting with model warning when no models are available
        setChat([{ 
          role: "assistant", 
          content: `Hello${userName ? " " + userName : ""}! No AI model is assigned to your organization. Please contact support.` 
        }]);
      } else {
        // Normal greeting when license is active and models are available
        initializeChat();
      }
    }
  }, [isReady, userName, initializeChat, isLicenseError, availableModels, setChat]);

  // Action handlers
  const handleSummarize = () => handleAction("summarize");
  const handleActionItems = () => handleAction("actionItems");
  const handleReplyOptions = () => handleAction("replyOptions");
  const handleGrammar = () => handleAction("grammar");
  const handleReview = () => handleAction("review");
  const handleProfessional = () => handleAction("professional");
  const handleNotes = () => handleAction("notes");
  const handleRewrite = () => handleAction("rewrite");
  const handlePivotTable = () => handleAction("pivottable");
  
  const handlePromptSubmit = () => {
    if (input.trim()) {
      handleAction("custom", input, selectedModel);
      setInput("");
    }
  };

  // Loading state
  if (!isReady && !requiresPopup) {
    return (
      <div className="loadingContainer">
        <div className="loadingSpinner"></div>
        <div className="loadingText">Loading</div>
      </div>
    );
  }

  // Show login button if popup authentication is required
  if (requiresPopup && onPopupSuccess) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        padding: '40px',
        boxSizing: 'border-box'
      }}>
        {/* EP Logo */}
        <img 
          src="assets/ep.png" 
          alt="EP Logo" 
          style={{
            width: '80px',
            height: '80px',
            marginBottom: '20px',
            objectFit: 'contain'
          }}
        />
        
        {/* My AI Secretary Title */}
        <h1 style={{ 
          marginBottom: '30px', 
          textAlign: 'center',
          fontSize: '28px',
          fontWeight: '600',
          color: '#333',
          marginTop: 0
        }}>
          My AI Secretary
        </h1>
        
        {/* Sign in required heading */}
        <h2 style={{ 
          marginBottom: '20px', 
          textAlign: 'center',
          fontSize: '20px',
          fontWeight: '500',
          color: '#555'
        }}>
          Sign in required
        </h2>
        
        {/* Description */}
        <p style={{ 
          marginBottom: '30px', 
          textAlign: 'center', 
          color: '#666',
          fontSize: '14px'
        }}>
          Please sign in to continue using the add-in.
        </p>
        
        {/* Login Button */}
        <LoginButton
          onSuccess={onPopupSuccess}
          onError={(error) => {
            console.error("Login error:", error);
            // You could show an error message here if needed
          }}
          loginHint={loginHint || undefined}
        />
      </div>
    );
  }

  console.log("userName:", userName);
  console.log("requiresPopup:", requiresPopup);
  if (!userName && !requiresPopup) {
    return (
      <ErrorMessage 
        title="Error - Cannot identify user" 
        message="Please open the app in Outlook and try again." 
      />
    );
  }
  
  // Note: item may be null (e.g., multi-select in Outlook, or Word/Excel/PowerPoint)
  // The chat will still work, just with empty context

  // Disable input and buttons if license error, no models available, streaming, or inserting
  const isDisabled = isLicenseError || availableModels.length === 0 || isStreaming || isInserting;

  return (
    <div className="flex flex-col h-screen">
      <ChatArea
        chat={chat}
        isStreaming={isStreaming}
        mode={mode}
        hasOriginalEmail={hasOriginalEmail}
        host={context?.host || 'Unknown'}
        item={item}
        onSummarize={handleSummarize}
        onReplyOptions={handleReplyOptions}
        onActionItems={handleActionItems}
        onGrammar={handleGrammar}
        onReview={handleReview}
        onProfessional={handleProfessional}
        onNotes={handleNotes}
        onRewrite={handleRewrite}
        onPivotTable={handlePivotTable}
        isDisabled={isDisabled}
        setIsInserting={setIsInserting}
        selectedModel={selectedModel}
      />
      <ChatInput
        input={input}
        setInput={setInput}
        onSubmit={handlePromptSubmit}
        isStreaming={isStreaming}
        mode={mode}
        onClearChat={initializeChat}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedKnowledgeBase={selectedKnowledgeBase}
        setSelectedKnowledgeBase={setSelectedKnowledgeBase}
        availableModels={availableModels}
        knowledgeBases={knowledgeBases}
        isDisabled={isDisabled}
      />
    </div>
  );
}